"use client"

import { useCart } from "@/lib/cart-context"
import { useLanguage } from "@/lib/language-context"
import { Link } from "react-router-dom"
import { Trash2, Plus, Minus, ShoppingBag } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"

export function CartContent() {
  const { items, removeItem, updateQuantity, total } = useCart()
  const { t } = useLanguage()

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-background py-12">
        <div className="container mx-auto px-4">
          <div className="text-center py-20">
            <ShoppingBag className="h-20 w-20 mx-auto text-muted-foreground mb-4 opacity-50" />
            <h1 className="text-3xl font-bold mb-4">{t("cart", "empty_title") || "Savat bo'sh"}</h1>
            <p className="text-muted-foreground mb-8">
              {t("cart", "empty_message") || "Savatchingizda hali mahsulot yo'q"}
            </p>
            <Link to="/products">
              <Button size="lg" className="bg-green-600 hover:bg-green-700">
                {t("nav", "products") || "Mahsulotlar"}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold mb-8">{t("cart", "title") || "Savat"}</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map((item) => (
              <Card key={item.id} className="overflow-hidden">
                <CardContent className="p-4 flex gap-4">
                  {/* Image */}
                  <div className="relative w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                    <img src={item.image || "/placeholder.svg"} alt={item.name} className="object-cover w-full h-full" />
                  </div>

                  {/* Item Details */}
                  <div className="flex-1 min-w-0">
                    <Link to={`/products/${item.name}`}>
                      <h3 className="font-semibold hover:text-green-600 truncate">{item.name}</h3>
                    </Link>
                    <p className="text-green-600 font-bold">
                      {item.price.toLocaleString()} {t("products", "currency") || "so'm"}
                    </p>
                  </div>

                  {/* Quantity Controls */}
                  <div className="flex items-center gap-2 border rounded-lg">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      className="h-8 w-8"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="h-8 w-8"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Total */}
                  <div className="text-right min-w-[100px]">
                    <p className="font-bold text-lg">{(item.price * item.quantity).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.quantity} x {item.price.toLocaleString()}
                    </p>
                  </div>

                  {/* Remove Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeItem(item.id)}
                    className="h-8 w-8 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>{t("cart", "summary") || "Buyurtma xulasasi"}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Items Count */}
                <div className="flex justify-between text-sm">
                  <span>{t("cart", "items") || "Mahsulotlar"}</span>
                  <span>{items.length}</span>
                </div>

                {/* Total Quantity */}
                <div className="flex justify-between text-sm">
                  <span>{t("cart", "quantity") || "Umumiy miqdor"}</span>
                  <span>{items.reduce((sum, item) => sum + item.quantity, 0)}</span>
                </div>

                {/* Subtotal */}
                <div className="flex justify-between text-sm pb-4 border-b">
                  <span>{t("cart", "subtotal") || "Jami"}</span>
                  <span>{total.toLocaleString()}</span>
                </div>

                {/* Shipping */}
                <div className="flex justify-between text-sm pb-4 border-b">
                  <span>{t("cart", "shipping") || "Yetkazib berish"}</span>
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    {t("cart", "free") || "Bepul"}
                  </Badge>
                </div>

                {/* Total */}
                <div className="flex justify-between font-bold text-lg pt-2">
                  <span>{t("cart", "total") || "Jami"}</span>
                  <span className="text-green-600">{total.toLocaleString()}</span>
                </div>

                {/* Checkout Button */}
                <Button size="lg" className="w-full bg-green-600 hover:bg-green-700 text-white mt-6">
                  {t("cart", "checkout") || "Buyurtma berish"}
                </Button>

                {/* Continue Shopping */}
                <Link to="/products">
                  <Button size="lg" variant="outline" className="w-full bg-transparent">
                    {t("nav", "products") || "Mahsulotlarga qaytar"}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
